import { useCallback, useEffect, useState } from 'react'
import { Box, Typography } from '@mui/material'
import { Trans, useTranslation } from 'react-i18next'
import { Button } from './atoms/Button'
import { Text } from './atoms/Text'
import { EnergyStoragePortControl } from '../classes/ports/EnergyStoragePortControl'
import { callAxios } from '../hooks/useAxios'
import { useApp } from '../hooks/useApp'
import { ID } from '../types/models'

/**
 * The active server-driven "go to SoC" run of a battery appliance, as
 * returned by `GET /appliances/:id/goto_soc`.
 */
export interface GotoSocRun {
  id: ID
  target_soc: number
  power: number
  status: string
  started_at: string
  last_soc: number | null
  last_power: number | null
  last_sent_at: string | null
}

/**
 * How often the widget re-reads the run while mounted. The run can end
 * without this browser's involvement — a manual command from another
 * session, the appliance leaving manual mode, or the five-day timeout.
 */
export const GOTO_SOC_POLL_MS = 30_000

export interface ManualBatteryControlProps {
  /** The energy-storage port (22/x) the command is sent to. */
  control: EnergyStoragePortControl
  /** The battery appliance the go-to-SoC run belongs to. */
  applianceId: ID
}

/**
 * Direct power command for a battery in maintenance mode.
 *
 * Available only once nothing else in the firmware is driving the inverter.
 * The battery optimizer (port 0x220) owns it while enabled and would overwrite
 * a direct command on its next planning tick; on a reserve battery the FCR
 * commander does the same from a 100 ms interrupt, so that unit's FCR port has
 * to be off as well.
 *
 * The command goes out as a single `ENERGYSTORAGE_CMD_SET_POWER` on `22/x/do`
 * and the firmware holds it until the next one. Sign follows the port's own
 * convention: negative charges, positive discharges, zero stops.
 *
 * Below the buttons is the server-driven "go to SoC" mode: the server sends
 * the same command every ten seconds towards a target state of charge until
 * the run is cancelled here (PERU), taken over by a manual button press, ended
 * by leaving manual mode, or timed out after five days.
 *
 * Declared at module level on purpose. A component defined inside a render
 * body is a new type on every render, so React remounts it — and under the
 * live websocket traffic of the front page that remount lands between
 * mousedown and mouseup and swallows the click.
 */
export function ManualBatteryControl(props: ManualBatteryControlProps): JSX.Element {
  const { control, applianceId } = props
  const { t } = useTranslation()
  const app = useApp()
  const [power, setPower] = useState<string>('')
  const [sending, setSending] = useState(false)

  const [run, setRun] = useState<GotoSocRun | null>(null)
  const [targetSoc, setTargetSoc] = useState<string>('')
  const [gotoPower, setGotoPower] = useState<string>('')
  const [gotoBusy, setGotoBusy] = useState(false)

  const gotoUrl = `/appliances/${applianceId}/goto_soc`

  const loadRun = useCallback(async () => {
    const res = await callAxios<GotoSocRun | null>({ url: gotoUrl })
    if (res.success) {
      setRun(res.data ?? null)
    }
  }, [gotoUrl])

  useEffect(() => {
    loadRun()
    const timer = setInterval(loadRun, GOTO_SOC_POLL_MS)
    return () => clearInterval(timer)
  }, [loadRun])

  const send = async (sign: number) => {
    const magnitude = Math.abs(parseInt(power || '0'))
    // `magnitude || 0` also catches an unparseable box; the explicit zero case
    // keeps STOP (and a zero magnitude) off the negative-zero path.
    const value = magnitude ? magnitude * sign : 0

    setSending(true)
    try {
      const res = await control.sendCommand({
        command: 'ENERGYSTORAGE_CMD_SET_POWER',
        new_real_power: value,
        new_reactive_power: 0,
      }) as { success?: boolean } | undefined
      // A manual command takes over from a go-to-SoC run: the server ends the
      // run before it sends the command.
      if (res?.success !== false) {
        setRun(null)
      }
    } finally {
      setSending(false)
    }
  }

  const execute = async () => {
    const target_soc = parseInt(targetSoc)
    const powerValue = Math.abs(parseInt(gotoPower))
    if (isNaN(target_soc) || isNaN(powerValue)) return

    setGotoBusy(true)
    try {
      const res = await callAxios<GotoSocRun>({ url: gotoUrl, method: 'POST' }, { target_soc, power: powerValue })
      if (res.success) {
        setRun(res.data ?? null)
      } else {
        app.addError(res)
      }
    } finally {
      setGotoBusy(false)
    }
  }

  const cancel = async () => {
    setGotoBusy(true)
    try {
      const res = await callAxios({ url: gotoUrl, method: 'DELETE' })
      if (res.success) {
        setRun(null)
      } else {
        app.addError(res)
      }
    } finally {
      setGotoBusy(false)
    }
  }

  // Only a command already in flight blocks the next one. Inverter error flags
  // deliberately do not: maintenance mode exists to command a battery that is
  // misbehaving, and the optimizer-era buttons gate on those flags precisely
  // because the optimizer, not a person, was driving.
  const busy = sending

  const targetValue = parseInt(targetSoc)
  const gotoPowerValue = parseInt(gotoPower)
  const canExecute = !gotoBusy && !isNaN(targetValue) && targetValue >= 0 && targetValue <= 100 &&
    !isNaN(gotoPowerValue) && gotoPowerValue !== 0

  return <Box data-cy="box-Manual command">
    <Typography sx={{ color: 'error.main', fontWeight: 'bold' }}>
      <Trans>MAINTENANCE MODE - MANUAL COMMAND</Trans>
    </Typography>

    <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
      <Typography><Trans>Manual power command</Trans></Typography>
      <Text
        data-cy="text-Power command"
        type="number"
        size="small"
        sx={{ width: '7rem' }}
        value={power}
        onChange={(e) => setPower(e.target.value)}
      />
      <Typography>W</Typography>
    </Box>

    <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button disabled={busy} sx={{ p: 0, fontSize: '90%' }} onClick={() => send(-1)} size="small" variant="text">
        <Trans>MANUAL_CHARGE</Trans>
      </Button>
      <Button disabled={busy} sx={{ p: 0, fontSize: '90%' }} onClick={() => send(1)} size="small" variant="text">
        <Trans>MANUAL_DISCHARGE</Trans>
      </Button>
      <Button disabled={busy} sx={{ p: 0, fontSize: '90%' }} onClick={() => send(0)} size="small" variant="text">
        <Trans>MANUAL_STOP</Trans>
      </Button>
    </Box>

    {run
      ? <Box data-cy="box-Goto SoC active" sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography>{t('Heading towards state of charge {soc}%').replace('{soc}', `${run.target_soc}`)}</Typography>
          <Button disabled={gotoBusy} sx={{ p: 0, fontSize: '90%' }} onClick={cancel} size="small" variant="text">
            <Trans>GOTO_SOC_CANCEL</Trans>
          </Button>
        </Box>
      : <Box data-cy="box-Goto SoC" sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography><Trans>GOTO_SOC_GO_TO</Trans></Typography>
          <Text
            data-cy="text-Target SoC"
            type="number"
            size="small"
            sx={{ width: '5rem' }}
            value={targetSoc}
            onChange={(e) => setTargetSoc(e.target.value)}
          />
          <Typography><Trans>GOTO_SOC_STATE_OF_CHARGE_AT</Trans></Typography>
          <Text
            data-cy="text-Goto power"
            type="number"
            size="small"
            sx={{ width: '7rem' }}
            value={gotoPower}
            onChange={(e) => setGotoPower(e.target.value)}
          />
          <Typography><Trans>GOTO_SOC_POWER</Trans></Typography>
          <Button disabled={!canExecute} sx={{ p: 0, fontSize: '90%' }} onClick={execute} size="small" variant="text">
            <Trans>GOTO_SOC_EXECUTE</Trans>
          </Button>
        </Box>}
  </Box>
}
